import { UAParser } from "ua-parser-js";

const TABLET_MIN_SHORT_SIDE = 600;
const TABLET_MIN_LONG_SIDE = 800;

function getNavigator() {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.navigator;
}

function getWindow() {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window;
}

function matchesMediaQuery(mediaQuery) {
  const windowInstance = getWindow();

  if (!windowInstance?.matchMedia) {
    return false;
  }

  return windowInstance.matchMedia(mediaQuery).matches;
}

function getViewportDimensions(windowInstance) {
  if (!windowInstance) {
    return {
      width: 0,
      height: 0,
      shortSide: 0,
      longSide: 0,
    };
  }

  const width = windowInstance.innerWidth || 0;
  const height = windowInstance.innerHeight || 0;

  return {
    width,
    height,
    shortSide: Math.min(width, height),
    longSide: Math.max(width, height),
  };
}

function getInteractionProfile(navigatorInstance, windowInstance) {
  const maxTouchPoints = navigatorInstance?.maxTouchPoints || 0;

  return {
    maxTouchPoints,
    isTouchDevice: maxTouchPoints > 1,
    viewport: getViewportDimensions(windowInstance),
  };
}

function isIPadLikeDevice(navigatorInstance) {
  if (!navigatorInstance) {
    return false;
  }

  const { platform = "", maxTouchPoints = 0 } = navigatorInstance;

  return platform === "MacIntel" && maxTouchPoints > 1;
}

function parseClientDevice() {
  const parser = new UAParser();
  return parser.getResult();
}

function isDesktopOperatingSystem(osName) {
  return ["Windows", "Mac OS", "Linux", "Chrome OS"].includes(osName);
}

function isLikelyTabletByCapabilities(deviceInfo, interactionProfile, isIPad) {
  const { isTouchDevice, viewport } = interactionProfile;

  if (isIPad) {
    return true;
  }

  if (!isTouchDevice) {
    return false;
  }

  if (isDesktopOperatingSystem(deviceInfo.os?.name)) {
    return false;
  }

  return (
    viewport.shortSide >= TABLET_MIN_SHORT_SIDE &&
    viewport.longSide >= TABLET_MIN_LONG_SIDE
  );
}

function getDetectedDeviceType(deviceInfo, isIPad, interactionProfile) {
  const parsedType = deviceInfo.device?.type;

  if (
    parsedType === "tablet" ||
    isLikelyTabletByCapabilities(deviceInfo, interactionProfile, isIPad)
  ) {
    return "tablet";
  }

  if (parsedType === "mobile") {
    return "mobile";
  }

  return parsedType;
}

export function getClientDeviceInfo() {
  const navigatorInstance = getNavigator();
  const windowInstance = getWindow();
  const deviceInfo = parseClientDevice();
  const isIPad = isIPadLikeDevice(navigatorInstance);
  const interactionProfile = getInteractionProfile(
    navigatorInstance,
    windowInstance
  );
  const deviceType = getDetectedDeviceType(
    deviceInfo,
    isIPad,
    interactionProfile
  );

  return {
    ...deviceInfo,
    device: {
      ...deviceInfo.device,
      type: deviceType,
      vendor: isIPad ? deviceInfo.device?.vendor || "Apple" : deviceInfo.device?.vendor,
      model: isIPad ? deviceInfo.device?.model || "iPad" : deviceInfo.device?.model,
    },
    interactionProfile,
  };
}

export function getCurrentDeviceType() {
  const { device } = getClientDeviceInfo();

  switch (device?.type) {
    case "tablet":
      return "tablet";
    case "mobile":
      return "smartphone";
    default:
      return "laptop";
  }
}

export function getCurrentDeviceReport() {
  const { browser, device, os } = getClientDeviceInfo();

  if (device?.type && device?.vendor) {
    return `${device.vendor} ${device.model || ""}`.trim();
  }

  return [os?.name, os?.version, browser?.name].filter(Boolean).join(" ");
}

export function getCurrentDeviceBrandAndModel() {
  const { device } = getClientDeviceInfo();
  return [device?.vendor, device?.model].filter(Boolean).join(" ");
}